import React, { useState, useEffect } from "react";
import "../styles/MapView.css";
import { Department, Route, Landmark } from "../types";
import InteractiveMap from "./InteractiveMap";

interface MapViewProps {
  kioskId: string;
  kioskData?: any;
  destination?: Department | Landmark | null;
  onBack: () => void;
  allDepartments?: Department[];
  landmarks?: Landmark[];
  route?: Route | null;
  currentStepIndex?: number;
  showAllLocations?: boolean;
  highlightKiosk?: boolean;
  onFloorChange?: (floor: string) => void;
}

const MapView: React.FC<MapViewProps> = ({
  kioskId,
  kioskData,
  destination,
  onBack,
  allDepartments = [],
  landmarks = [],
  route,
  currentStepIndex = 0,
  showAllLocations = false,
  highlightKiosk = false,
  onFloorChange,
}) => {
  const [mapRoute, setMapRoute] = useState<any | null>(null);
  const [selectedDept, setSelectedDept] = useState<Department | undefined>(
    destination && "category" in destination
      ? (destination as Department)
      : undefined,
  );

  const [selectedDestinationForMap, setSelectedDestinationForMap] = useState<
    Department | Landmark | null
  >(null);

  useEffect(() => {
    setSelectedDestinationForMap(destination || null);
  }, [destination]);

  const handleDepartmentSelect = (dept: Department) => {
    setSelectedDept(dept);
  };

  const displayDepartments = showAllLocations
    ? allDepartments
    : selectedDept
      ? [selectedDept]
      : destination && "category" in destination
        ? [destination as Department]
        : [];

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          position: "relative",
          minHeight: 0,
        }}
      >
        <InteractiveMap
          departments={
            displayDepartments.length > 0 ? displayDepartments : allDepartments
          }
          landmarks={landmarks}
          selectedDepartment={selectedDept}
          selectedDestination={selectedDestinationForMap}
          onDepartmentSelect={handleDepartmentSelect}
          height="100%"
          kioskId={kioskId}
          kioskData={kioskData}
          onRouteFound={setMapRoute}
          route={route}
          currentStepIndex={currentStepIndex}
          highlightKiosk={highlightKiosk}
          showAllLocations={showAllLocations}
          onFloorChange={(floor) => {
            setSelectedDept(undefined);
            onFloorChange?.(floor);
          }}
        />

        {/* Destination Info Ribbon — sits above Start Navigation button */}
        {destination && (
          <div
            style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: "#1D4ED8",
              color: "white",
              padding: "8px 16px",
              zIndex: 1001,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 16,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                flex: 1,
                minWidth: 0,
              }}
            >
              <span style={{ fontSize: 13, fontWeight: 600, flexShrink: 0 }}>
                Destination:
              </span>
              <span
                style={{
                  fontSize: 13,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {destination.name}
              </span>
              {destination.description && (
                <span
                  style={{
                    fontSize: 13,
                    opacity: 0.9,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  dir="rtl"
                >
                  {destination.description}
                </span>
              )}
            </div>
            {route && route.totalDistance > 0 && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  fontSize: 13,
                  flexShrink: 0,
                }}
              >
                <span>~{route.totalDistance}m</span>
                <span>•</span>
                <span>
                  {route.segments.reduce((total, segment) => {
                    const distanceInMeters = segment.distance || 0;
                    const timeInMinutes = Math.max(
                      1,
                      Math.round(distanceInMeters / 84),
                    );
                    return total + timeInMinutes;
                  }, 0)}{" "}
                  min
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MapView;
